import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ChangeEvent,
  type FormEvent,
} from "react";
import { Link } from "react-router-dom";
import { supabase } from "../lib/supabase";
import { useOrganization } from "../organization/OrganizationProvider";
import "./BankReconciliationPage.css";

type TreasuryAccount = {
  id: string;
  name: string;
  code: string;
  account_type: string;
  currency_code: string;
  current_balance: number | string;
};

type ReconciliationRow = {
  id: string;
  reconciliation_number: number | string;
  treasury_account_id: string;
  account_name: string;
  period_start: string;
  period_end: string;
  currency_code: string;
  opening_book_balance: number | string;
  closing_book_balance: number | string;
  opening_bank_balance: number | string;
  closing_bank_balance: number | string;
  reconciled_book_amount: number | string;
  reconciled_bank_amount: number | string;
  difference: number | string;
  status: string;
  pending_lines: number | string;
  matched_lines: number | string;
  ignored_lines: number | string;
  updated_at: string;
};

type StatementLine = {
  id: string;
  line_number: number | string;
  transaction_date: string;
  value_date: string | null;
  line_type: "credit" | "debit";
  amount: number | string;
  currency_code: string;
  bank_reference: string | null;
  description: string | null;
  external_id: string | null;
  ignore_reason: string | null;
  correction_count: number | string;
  status: "pending" | "matched" | "ignored";
  match_movement_id: string | null;
};

type StatementLineEdit = {
  id: string;
  transaction_date: string;
  value_date: string;
  line_type: "credit" | "debit";
  amount: string;
  bank_reference: string;
  description: string;
  external_id: string;
  reason: string;
};

type Movement = {
  id: string;
  movement_number: number | string;
  movement_type: string;
  movement_date: string;
  amount: number | string;
  currency_code: string;
  reference: string | null;
  description: string;
};

type ReconciliationHistoryEntry = {
  id: string;
  old_status: string | null;
  new_status: string;
  reason: string | null;
  changed_by: string | null;
  changed_at: string;
};

type Detail = {
  reconciliation: ReconciliationRow & {
    organization_id: string;
    treasury_account_id: string;
    reconciled_at: string | null;
    reconciled_by: string | null;
    closed_at: string | null;
    closed_by: string | null;
    notes: string | null;
  };
  account: {
    id: string;
    name: string;
    code: string;
    currency_code: string;
    bank_name: string | null;
  } | null;
  lines: StatementLine[];
  available_movements: Movement[];
  history: ReconciliationHistoryEntry[];
};

type TreasurySummary = {
  accounts: TreasuryAccount[];
};

type StatementImportRow = {
  transaction_date: string;
  value_date: string | null;
  line_type: "credit" | "debit";
  amount: number;
  bank_reference: string | null;
  description: string | null;
  external_id: string | null;
};

const CSV_TEMPLATE = [
  "fecha;fecha_valor;tipo;monto;referencia;descripcion;id_externo",
  "2026-10-01;2026-10-01;abono;100.50;OP-001;Cobro cliente;MOV-001",
  "2026-10-02;2026-10-02;cargo;35.20;OP-002;Pago proveedor;MOV-002",
].join("\n");

function parseDelimitedLine(line: string, delimiter: string) {
  const values: string[] = [];
  let current = "";
  let quoted = false;

  for (let index = 0; index < line.length; index += 1) {
    const char = line[index];

    if (char === '"') {
      if (quoted && line[index + 1] === '"') {
        current += '"';
        index += 1;
      } else {
        quoted = !quoted;
      }
      continue;
    }

    if (char === delimiter && !quoted) {
      values.push(current.trim());
      current = "";
      continue;
    }

    current += char;
  }

  values.push(current.trim());
  return values;
}

function normalizeHeader(value: string) {
  return value
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/\s+/g, "_");
}

function normalizeStatementDate(value: string, rowNumber: number) {
  const raw = value.trim();

  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) return raw;

  const match = raw.match(/^(\d{1,2})[\/-](\d{1,2})[\/-](\d{4})$/);
  if (!match) {
    throw new Error(`Fila ${rowNumber}: fecha inválida "${value}".`);
  }

  const [, day, month, year] = match;
  const result = `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
  const parsed = new Date(`${result}T00:00:00Z`);

  if (
    Number.isNaN(parsed.getTime()) ||
    parsed.toISOString().slice(0, 10) !== result
  ) {
    throw new Error(`Fila ${rowNumber}: fecha inválida "${value}".`);
  }

  return result;
}

function normalizeStatementAmount(value: string, rowNumber: number) {
  let raw = value.trim().replace(/\s/g, "").replace(/S\//gi, "").replace(/USD/gi, "");

  if (raw.includes(",") && raw.includes(".")) {
    if (raw.lastIndexOf(",") > raw.lastIndexOf(".")) {
      raw = raw.replace(/\./g, "").replace(",", ".");
    } else {
      raw = raw.replace(/,/g, "");
    }
  } else if (raw.includes(",")) {
    raw = raw.replace(",", ".");
  }

  const amount = Number(raw);

  if (!Number.isFinite(amount) || amount <= 0) {
    throw new Error(`Fila ${rowNumber}: monto inválido "${value}".`);
  }

  return amount;
}

function parseStatementCsv(text: string): StatementImportRow[] {
  const rawLines = text
    .replace(/^\uFEFF/, "")
    .split(/\r?\n/)
    .filter((line) => line.trim().length > 0);

  if (rawLines.length < 2) {
    throw new Error("El CSV debe incluir encabezado y al menos una línea.");
  }

  const commaHeader = parseDelimitedLine(rawLines[0], ",");
  const semicolonHeader = parseDelimitedLine(rawLines[0], ";");
  const delimiter = semicolonHeader.length > commaHeader.length ? ";" : ",";
  const headers = parseDelimitedLine(rawLines[0], delimiter).map(normalizeHeader);

  const findIndex = (...aliases: string[]) =>
    headers.findIndex((header) => aliases.includes(header));

  const dateIndex = findIndex("fecha", "transaction_date", "fecha_operacion");
  const valueDateIndex = findIndex("fecha_valor", "value_date");
  const typeIndex = findIndex("tipo", "line_type", "movimiento");
  const amountIndex = findIndex("monto", "amount", "importe");
  const referenceIndex = findIndex("referencia", "bank_reference", "reference");
  const descriptionIndex = findIndex("descripcion", "description", "detalle");
  const externalIdIndex = findIndex("id_externo", "external_id", "codigo");

  if (dateIndex < 0 || typeIndex < 0 || amountIndex < 0) {
    throw new Error(
      "Faltan columnas obligatorias: fecha, tipo y monto.",
    );
  }

  if (rawLines.length - 1 > 1000) {
    throw new Error("El archivo supera el máximo de 1,000 líneas.");
  }

  return rawLines.slice(1).map((line, index) => {
    const rowNumber = index + 2;
    const values = parseDelimitedLine(line, delimiter);
    const read = (position: number) =>
      position >= 0 ? (values[position] ?? "").trim() : "";

    const typeRaw = normalizeHeader(read(typeIndex));
    const lineType =
      ["credit", "credito", "abono"].includes(typeRaw)
        ? "credit"
        : ["debit", "debito", "cargo"].includes(typeRaw)
          ? "debit"
          : null;

    if (!lineType) {
      throw new Error(
        `Fila ${rowNumber}: tipo inválido "${read(typeIndex)}". Usa abono/cargo o credit/debit.`,
      );
    }

    const valueDateRaw = read(valueDateIndex);

    return {
      transaction_date: normalizeStatementDate(read(dateIndex), rowNumber),
      value_date: valueDateRaw
        ? normalizeStatementDate(valueDateRaw, rowNumber)
        : null,
      line_type: lineType,
      amount: normalizeStatementAmount(read(amountIndex), rowNumber),
      bank_reference: read(referenceIndex) || null,
      description: read(descriptionIndex) || null,
      external_id: read(externalIdIndex) || null,
    };
  });
}

function money(value: number | string, currency = "PEN") {
  return new Intl.NumberFormat("es-PE", {
    style: "currency",
    currency,
  }).format(Number(value || 0));
}

function today() {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: "America/Lima",
  }).format(new Date());
}

function firstDayOfMonth() {
  const date = new Date();
  const formatter = new Intl.DateTimeFormat("en-CA", {
    timeZone: "America/Lima",
    year: "numeric",
    month: "2-digit",
  });
  return formatter.format(date) + "-01";
}

export function BankReconciliationPage() {
  const { selectedOrganization } = useOrganization();
  const organizationId = selectedOrganization?.organizationId ?? null;

  const [rows, setRows] = useState<ReconciliationRow[]>([]);
  const [accounts, setAccounts] = useState<TreasuryAccount[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [detail, setDetail] = useState<Detail | null>(null);

  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [showLine, setShowLine] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [showReopen, setShowReopen] = useState(false);
  const [reopenReason, setReopenReason] = useState("");
  const [showCancel, setShowCancel] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [importRows, setImportRows] = useState<StatementImportRow[]>([]);
  const [importFileName, setImportFileName] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [accountId, setAccountId] = useState("");
  const [periodStart, setPeriodStart] = useState(firstDayOfMonth());
  const [periodEnd, setPeriodEnd] = useState(today());
  const [openingBank, setOpeningBank] = useState("0");
  const [closingBank, setClosingBank] = useState("0");

  const [lineDate, setLineDate] = useState(today());
  const [lineType, setLineType] = useState<"credit" | "debit">("credit");
  const [lineAmount, setLineAmount] = useState("");
  const [lineReference, setLineReference] = useState("");
  const [lineDescription, setLineDescription] = useState("");
  const [lineEdit, setLineEdit] = useState<StatementLineEdit | null>(null);

  const load = useCallback(async () => {
    if (!organizationId) {
      setRows([]);
      setAccounts([]);
      return;
    }

    setLoading(true);
    setError(null);

    const [listResult, treasuryResult] = await Promise.all([
      supabase.rpc("list_organization_bank_reconciliations", {
        p_organization_id: organizationId,
      }),
      supabase.rpc("get_organization_treasury_summary", {
        p_organization_id: organizationId,
      }),
    ]);

    const firstError = listResult.error ?? treasuryResult.error;
    if (firstError) setError(firstError.message);

    setRows(
      listResult.error ? [] : ((listResult.data ?? []) as ReconciliationRow[]),
    );

    const treasury = treasuryResult.error
      ? null
      : (treasuryResult.data as TreasurySummary);

    setAccounts(
      (treasury?.accounts ?? []).filter(
        (account) => account.account_type === "bank",
      ),
    );

    setLoading(false);
  }, [organizationId]);

  const loadDetail = useCallback(async (id: string) => {
    setError(null);

    const { data, error: rpcError } = await supabase.rpc(
      "get_bank_reconciliation_detail",
      {
        p_reconciliation_id: id,
      },
    );

    if (rpcError) {
      setDetail(null);
      setError(rpcError.message);
      return;
    }

    setDetail(data as Detail);
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (selectedId) void loadDetail(selectedId);
    else setDetail(null);
  }, [selectedId, loadDetail]);

  const selectedRow = useMemo(
    () => rows.find((row) => row.id === selectedId) ?? null,
    [rows, selectedId],
  );

  const canEditSelected = detail
    ? ["draft", "in_progress", "reopened"].includes(
        detail.reconciliation.status,
      )
    : false;

  const canReopenSelected = detail
    ? ["reconciled", "closed"].includes(detail.reconciliation.status)
    : false;

  async function createReconciliation(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!organizationId) return;

    if (!accountId) {
      setError("Selecciona una cuenta bancaria.");
      return;
    }

    setBusy(true);
    setError(null);
    setSuccess(null);

    const { data, error: rpcError } = await supabase.rpc(
      "create_bank_reconciliation",
      {
        p_organization_id: organizationId,
        p_treasury_account_id: accountId,
        p_period_start: periodStart,
        p_period_end: periodEnd,
        p_opening_bank_balance: Number(openingBank),
        p_closing_bank_balance: Number(closingBank),
        p_notes: null,
      },
    );

    setBusy(false);

    if (rpcError) {
      setError(rpcError.message);
      return;
    }

    const id = data as string;
    setShowCreate(false);
    setSelectedId(id);
    setSuccess("Conciliación creada.");
    await load();
  }

  async function addLine(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!selectedId) return;

    const amount = Number(lineAmount);
    if (!Number.isFinite(amount) || amount <= 0) {
      setError("Ingresa un monto válido.");
      return;
    }

    setBusy(true);
    setError(null);
    setSuccess(null);

    const { error: rpcError } = await supabase.rpc(
      "add_bank_statement_line",
      {
        p_reconciliation_id: selectedId,
        p_transaction_date: lineDate,
        p_line_type: lineType,
        p_amount: amount,
        p_bank_reference: lineReference || null,
        p_description: lineDescription || null,
        p_external_id: null,
        p_value_date: lineDate,
      },
    );

    setBusy(false);

    if (rpcError) {
      setError(rpcError.message);
      return;
    }

    setLineAmount("");
    setLineReference("");
    setLineDescription("");
    setShowLine(false);
    setSuccess("Línea bancaria agregada.");
    await loadDetail(selectedId);
    await load();
  }

  function startEditLine(line: StatementLine) {
    setLineEdit({
      id: line.id,
      transaction_date: line.transaction_date,
      value_date: line.value_date ?? line.transaction_date,
      line_type: line.line_type,
      amount: String(line.amount),
      bank_reference: line.bank_reference ?? "",
      description: line.description ?? "",
      external_id: line.external_id ?? "",
      reason: "",
    });
    setError(null);
    setSuccess(null);
  }

  async function updateStatementLine(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!lineEdit) return;

    const amount = Number(lineEdit.amount);
    const reason = lineEdit.reason.trim();

    if (!Number.isFinite(amount) || amount <= 0) {
      setError("Ingresa un monto válido.");
      return;
    }

    if (reason.length < 10) {
      setError("Indica un motivo de corrección de al menos 10 caracteres.");
      return;
    }

    setBusy(true);
    setError(null);
    setSuccess(null);

    const { error: rpcError } = await supabase.rpc(
      "update_bank_statement_line",
      {
        p_statement_line_id: lineEdit.id,
        p_transaction_date: lineEdit.transaction_date,
        p_line_type: lineEdit.line_type,
        p_amount: amount,
        p_bank_reference: lineEdit.bank_reference || null,
        p_description: lineEdit.description || null,
        p_external_id: lineEdit.external_id || null,
        p_value_date: lineEdit.value_date || null,
        p_reason: reason,
      },
    );

    setBusy(false);

    if (rpcError) {
      if (rpcError.message.includes("BANK_STATEMENT_MATCHED_REQUIRES_UNMATCH")) {
        setError("Desvincula la línea antes de corregirla.");
      } else if (
        rpcError.message.includes("BANK_STATEMENT_EXTERNAL_ID_DUPLICATE")
      ) {
        setError("El ID externo ya existe en esta conciliación.");
      } else {
        setError(rpcError.message);
      }
      return;
    }

    setLineEdit(null);
    setSuccess("Línea bancaria corregida y auditada.");

    if (selectedId) {
      await loadDetail(selectedId);
      await load();
    }
  }

  async function selectImportFile(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];

    setImportRows([]);
    setImportFileName("");
    setError(null);
    setSuccess(null);

    if (!file) return;

    if (file.size > 2_000_000) {
      setError("El archivo supera el máximo de 2 MB.");
      event.target.value = "";
      return;
    }

    try {
      const rows = parseStatementCsv(await file.text());
      setImportRows(rows);
      setImportFileName(file.name);
    } catch (parseError) {
      setError(
        parseError instanceof Error
          ? parseError.message
          : "No se pudo leer el CSV.",
      );
      event.target.value = "";
    }
  }

  async function importStatement() {
    if (!selectedId || importRows.length === 0) return;

    setBusy(true);
    setError(null);
    setSuccess(null);

    const { data, error: rpcError } = await supabase.rpc(
      "import_bank_statement_lines",
      {
        p_reconciliation_id: selectedId,
        p_lines: importRows,
      },
    );

    setBusy(false);

    if (rpcError) {
      setError(rpcError.message);
      return;
    }

    const result = data as {
      requested: number;
      imported: number;
      skipped_duplicates: number;
      currency_code: string;
    };

    setSuccess(
      `Extracto importado: ${result.imported} línea(s). Duplicados omitidos: ${result.skipped_duplicates}.`,
    );
    setImportRows([]);
    setImportFileName("");
    setShowImport(false);
    await loadDetail(selectedId);
    await load();
  }

  async function autoMatch() {
    if (!selectedId) return;

    setBusy(true);
    setError(null);
    setSuccess(null);

    const { data, error: rpcError } = await supabase.rpc(
      "auto_match_bank_reconciliation",
      {
        p_reconciliation_id: selectedId,
      },
    );

    setBusy(false);

    if (rpcError) {
      setError(rpcError.message);
      return;
    }

    const result = data as {
      matched: number;
      ambiguous: number;
      unmatched: number;
      remaining_pending: number;
    };

    setSuccess(
      `Autoconciliación: ${result.matched} vinculada(s), ${result.ambiguous} ambigua(s), ${result.unmatched} sin coincidencia. Pendientes: ${result.remaining_pending}.`,
    );
    await loadDetail(selectedId);
    await load();
  }

  async function matchLine(line: StatementLine, movementId: string) {
    if (!selectedId || !movementId) return;

    setBusy(true);
    setError(null);
    setSuccess(null);

    const { error: rpcError } = await supabase.rpc(
      "match_bank_statement_line",
      {
        p_statement_line_id: line.id,
        p_treasury_movement_id: movementId,
      },
    );

    setBusy(false);

    if (rpcError) {
      setError(rpcError.message);
      return;
    }

    setSuccess("Movimiento conciliado.");
    await loadDetail(selectedId);
    await load();
  }

  async function unmatchLine(lineId: string) {
    if (!selectedId) return;

    setBusy(true);
    setError(null);
    setSuccess(null);

    const { error: rpcError } = await supabase.rpc(
      "unmatch_bank_statement_line",
      {
        p_statement_line_id: lineId,
      },
    );

    setBusy(false);

    if (rpcError) {
      setError(rpcError.message);
      return;
    }

    setSuccess("Vinculación retirada.");
    await loadDetail(selectedId);
    await load();
  }

  async function ignoreLine(lineId: string) {
    if (!selectedId) return;

    setBusy(true);
    setError(null);
    setSuccess(null);

    const { error: rpcError } = await supabase.rpc(
      "ignore_bank_statement_line",
      {
        p_statement_line_id: lineId,
        p_reason: "No corresponde a un movimiento registrado",
      },
    );

    setBusy(false);

    if (rpcError) {
      setError(rpcError.message);
      return;
    }

    setSuccess("Línea marcada como ignorada.");
    await loadDetail(selectedId);
    await load();
  }

  async function reconcile() {
    if (!selectedId) return;

    setBusy(true);
    setError(null);
    setSuccess(null);

    const { error: rpcError } = await supabase.rpc(
      "reconcile_bank_reconciliation",
      {
        p_reconciliation_id: selectedId,
      },
    );

    setBusy(false);

    if (rpcError) {
      setError(rpcError.message);
      return;
    }

    setSuccess("Conciliación validada.");
    await loadDetail(selectedId);
    await load();
  }

  async function cancelReconciliation(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!selectedId) return;

    const reason = cancelReason.trim();

    if (reason.length < 10) {
      setError("Indica un motivo de cancelación de al menos 10 caracteres.");
      return;
    }

    setBusy(true);
    setError(null);
    setSuccess(null);

    const { error: rpcError } = await supabase.rpc(
      "cancel_bank_reconciliation",
      {
        p_reconciliation_id: selectedId,
        p_reason: reason,
      },
    );

    setBusy(false);

    if (rpcError) {
      setError(rpcError.message);
      return;
    }

    setShowCancel(false);
    setCancelReason("");
    setShowImport(false);
    setShowLine(false);
    setShowReopen(false);
    setSuccess("Conciliación cancelada.");
    await loadDetail(selectedId);
    await load();
  }

  async function reopenReconciliation(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!selectedId) return;

    const reason = reopenReason.trim();

    if (reason.length < 10) {
      setError("Indica un motivo de reapertura de al menos 10 caracteres.");
      return;
    }

    setBusy(true);
    setError(null);
    setSuccess(null);

    const { error: rpcError } = await supabase.rpc(
      "reopen_bank_reconciliation",
      {
        p_reconciliation_id: selectedId,
        p_reason: reason,
      },
    );

    setBusy(false);

    if (rpcError) {
      setError(rpcError.message);
      return;
    }

    setShowReopen(false);
    setReopenReason("");
    setShowImport(false);
    setShowLine(false);
    setSuccess("Conciliación reabierta para corrección.");
    await loadDetail(selectedId);
    await load();
  }


  async function closeReconciliation() {
    if (!selectedId) return;

    setBusy(true);
    setError(null);
    setSuccess(null);

    const { error: rpcError } = await supabase.rpc(
      "close_bank_reconciliation",
      {
        p_reconciliation_id: selectedId,
      },
    );

    setBusy(false);

    if (rpcError) {
      setError(rpcError.message);
      return;
    }

    setSuccess("Conciliación cerrada.");
    await loadDetail(selectedId);
    await load();
  }

  if (!selectedOrganization) {
    return (
      <div className="page">
        <section className="empty-state">
          <strong>Selecciona una empresa.</strong>
        </section>
      </div>
    );
  }

  return (
    <div className="page">
      <header className="page-header page-header-actions">
        <div>
          <span className="eyebrow">Conciliación bancaria</span>
          <h1>{selectedOrganization.name}</h1>
          <p>Compara los movimientos del sistema con el estado bancario.</p>
        </div>

        <button
          className="primary-button"
          type="button"
          disabled={accounts.length === 0}
          onClick={() => setShowCreate((value) => !value)}
        >
          Nueva conciliación
        </button>
      </header>

      {error ? <div className="inline-alert">{error}</div> : null}
      {success ? (
        <div className="form-success bank-message">{success}</div>
      ) : null}

      {!loading && accounts.length === 0 ? (
        <section className="empty-state">
          <strong>Configura una cuenta bancaria para conciliar.</strong>
          <p>
            La conciliación bancaria solo acepta cuentas activas de tipo Banco.
          </p>
          <Link className="primary-button" to="/tesoreria">
            Ir a Tesorería
          </Link>
        </section>
      ) : null}

      {showCreate ? (
        <section className="panel-card">
          <div className="section-heading">
            <div>
              <span className="eyebrow">Periodo</span>
              <h2>Nueva conciliación</h2>
            </div>
          </div>

          <form className="bank-form-grid" onSubmit={createReconciliation}>
            <label>
              Cuenta bancaria
              <select
                required
                value={accountId}
                onChange={(event) => setAccountId(event.target.value)}
              >
                <option value="">Seleccionar</option>
                {accounts.map((account) => (
                  <option key={account.id} value={account.id}>
                    {account.code} · {account.name} · {account.currency_code}
                  </option>
                ))}
              </select>
            </label>

            <label>
              Inicio
              <input
                type="date"
                required
                value={periodStart}
                onChange={(event) => setPeriodStart(event.target.value)}
              />
            </label>

            <label>
              Fin
              <input
                type="date"
                required
                value={periodEnd}
                onChange={(event) => setPeriodEnd(event.target.value)}
              />
            </label>

            <label>
              Saldo bancario inicial
              <input
                type="number"
                step="0.01"
                value={openingBank}
                onChange={(event) => setOpeningBank(event.target.value)}
              />
            </label>

            <label>
              Saldo bancario final
              <input
                type="number"
                step="0.01"
                value={closingBank}
                onChange={(event) => setClosingBank(event.target.value)}
              />
            </label>

            <div className="bank-form-actions">
              <button className="primary-button" type="submit" disabled={busy}>
                {busy ? "Creando…" : "Crear"}
              </button>
            </div>
          </form>
        </section>
      ) : null}

      <section className="bank-layout">
        <aside className="panel-card bank-list-panel">
          <div className="section-heading">
            <div>
              <span className="eyebrow">Periodos</span>
              <h2>Conciliaciones</h2>
            </div>
          </div>

          <div className="bank-reconciliation-list">
            {rows.map((row) => (
              <button
                className={
                  selectedId === row.id
                    ? "bank-reconciliation-item active"
                    : "bank-reconciliation-item"
                }
                type="button"
                key={row.id}
                onClick={() => setSelectedId(row.id)}
              >
                <div>
                  <strong>#{row.reconciliation_number}</strong>
                  <span>{row.account_name}</span>
                </div>
                <div>
                  <span className="status-badge">{row.status}</span>
                  <small>
                    {row.period_start} → {row.period_end}
                  </small>
                </div>
              </button>
            ))}

            {!loading && rows.length === 0 ? (
              <div className="empty-state">
                <strong>Sin conciliaciones.</strong>
              </div>
            ) : null}
          </div>
        </aside>

        <section className="panel-card bank-detail-panel">
          {!detail ? (
            <div className="empty-state">
              <strong>Selecciona una conciliación.</strong>
            </div>
          ) : (
            <>
              <div className="section-heading bank-detail-heading">
                <div>
                  <span className="eyebrow">
                    {detail.account?.name ?? "Cuenta bancaria"}
                  </span>
                  <h2>Conciliación #{detail.reconciliation.reconciliation_number}</h2>
                  <p>
                    {detail.reconciliation.period_start} →{" "}
                    {detail.reconciliation.period_end}
                  </p>
                </div>

                <div className="bank-detail-actions">
                  {canEditSelected ? (
                    <>
                      <button
                        className="ghost-button"
                        type="button"
                        onClick={() => {
                          setShowImport((value) => !value);
                          setShowLine(false);
                          setImportRows([]);
                          setImportFileName("");
                          setError(null);
                        }}
                      >
                        Importar CSV
                      </button>
                      <button
                        className="ghost-button"
                        type="button"
                        onClick={() => {
                          setShowLine((value) => !value);
                          setShowImport(false);
                        }}
                      >
                        + Línea bancaria
                      </button>
                    </>
                  ) : null}

                  {canEditSelected &&
                  detail.lines.some((line) => line.status === "pending") ? (
                    <button
                      className="ghost-button"
                      type="button"
                      disabled={busy}
                      onClick={() => void autoMatch()}
                    >
                      Autoconciliar
                    </button>
                  ) : null}

                  {canEditSelected ? (
                    <button
                      className="primary-button"
                      type="button"
                      disabled={busy}
                      onClick={() => void reconcile()}
                    >
                      Conciliar
                    </button>
                  ) : null}

                  {canEditSelected ? (
                    <button
                      className="ghost-button"
                      type="button"
                      disabled={busy}
                      onClick={() => {
                        setShowCancel((value) => !value);
                        setShowReopen(false);
                        setError(null);
                      }}
                    >
                      Cancelar
                    </button>
                  ) : null}

                  {detail.reconciliation.status === "reconciled" ? (
                    <button
                      className="primary-button"
                      type="button"
                      disabled={busy}
                      onClick={() => void closeReconciliation()}
                    >
                      Cerrar
                    </button>
                  ) : null}

                  {canReopenSelected ? (
                    <button
                      className="ghost-button"
                      type="button"
                      disabled={busy}
                      onClick={() => {
                        setShowReopen((value) => !value);
                        setError(null);
                      }}
                    >
                      Reabrir
                    </button>
                  ) : null}
                </div>
              </div>

              <section className="bank-summary-grid">
                <div className="metric-card">
                  <span>Saldo libro</span>
                  <strong>
                    {money(
                      detail.reconciliation.closing_book_balance,
                      detail.reconciliation.currency_code,
                    )}
                  </strong>
                </div>
                <div className="metric-card">
                  <span>Saldo banco</span>
                  <strong>
                    {money(
                      detail.reconciliation.closing_bank_balance,
                      detail.reconciliation.currency_code,
                    )}
                  </strong>
                </div>
                <div className="metric-card">
                  <span>Diferencia</span>
                  <strong>
                    {money(
                      detail.reconciliation.difference,
                      detail.reconciliation.currency_code,
                    )}
                  </strong>
                </div>
              </section>

              {showCancel && canEditSelected ? (
                <form
                  className="bank-cancel-panel"
                  onSubmit={cancelReconciliation}
                >
                  <div>
                    <span className="eyebrow">Anulación controlada</span>
                    <h3>Cancelar conciliación</h3>
                    <p>
                      La conciliación quedará bloqueada y conservará sus líneas
                      e historial para auditoría. El periodo quedará disponible
                      para una nueva conciliación.
                    </p>
                  </div>

                  <label>
                    Motivo
                    <textarea
                      required
                      minLength={10}
                      rows={3}
                      value={cancelReason}
                      onChange={(event) => setCancelReason(event.target.value)}
                      placeholder="Explica por qué debe cancelarse esta conciliación."
                    />
                  </label>

                  <div className="bank-form-actions">
                    <button
                      className="primary-button"
                      type="submit"
                      disabled={busy}
                    >
                      {busy ? "Cancelando…" : "Confirmar cancelación"}
                    </button>
                  </div>
                </form>
              ) : null}

              {showReopen && canReopenSelected ? (
                <form
                  className="bank-reopen-panel"
                  onSubmit={reopenReconciliation}
                >
                  <div>
                    <span className="eyebrow">Corrección controlada</span>
                    <h3>Reabrir conciliación</h3>
                    <p>
                      La reapertura queda registrada en el historial y vuelve a
                      habilitar líneas, matching e importación.
                    </p>
                  </div>

                  <label>
                    Motivo
                    <textarea
                      required
                      minLength={10}
                      rows={3}
                      value={reopenReason}
                      onChange={(event) => setReopenReason(event.target.value)}
                      placeholder="Explica por qué debe corregirse esta conciliación."
                    />
                  </label>

                  <div className="bank-form-actions">
                    <button
                      className="primary-button"
                      type="submit"
                      disabled={busy}
                    >
                      {busy ? "Reabriendo…" : "Confirmar reapertura"}
                    </button>
                  </div>
                </form>
              ) : null}

              {showImport && canEditSelected ? (
                <section className="bank-import-panel">
                  <div className="section-heading">
                    <div>
                      <span className="eyebrow">Extracto bancario</span>
                      <h3>Importar CSV</h3>
                      <p>
                        Columnas obligatorias: fecha, tipo y monto. Máximo
                        1,000 líneas y 2 MB.
                      </p>
                    </div>
                    <a
                      className="compact-button"
                      href={`data:text/csv;charset=utf-8,${encodeURIComponent(
                        CSV_TEMPLATE,
                      )}`}
                      download="plantilla-extracto-bancario.csv"
                    >
                      Descargar plantilla
                    </a>
                  </div>

                  <div className="bank-import-controls">
                    <label>
                      Archivo CSV
                      <input
                        type="file"
                        accept=".csv,text/csv"
                        onChange={(event) => void selectImportFile(event)}
                      />
                    </label>

                    <div>
                      <strong>
                        {importFileName || "Ningún archivo seleccionado"}
                      </strong>
                      <span>
                        {importRows.length > 0
                          ? `${importRows.length} línea(s) listas para importar.`
                          : "Se aceptan fechas YYYY-MM-DD o DD/MM/YYYY."}
                      </span>
                    </div>

                    <button
                      className="primary-button"
                      type="button"
                      disabled={busy || importRows.length === 0}
                      onClick={() => void importStatement()}
                    >
                      {busy
                        ? "Importando…"
                        : `Importar ${importRows.length || ""}`.trim()}
                    </button>
                  </div>
                </section>
              ) : null}

              {showLine && canEditSelected ? (
                <form className="bank-form-grid bank-line-form" onSubmit={addLine}>
                  <label>
                    Fecha
                    <input
                      type="date"
                      required
                      value={lineDate}
                      onChange={(event) => setLineDate(event.target.value)}
                    />
                  </label>

                  <label>
                    Tipo
                    <select
                      value={lineType}
                      onChange={(event) =>
                        setLineType(event.target.value as "credit" | "debit")
                      }
                    >
                      <option value="credit">Abono / crédito</option>
                      <option value="debit">Cargo / débito</option>
                    </select>
                  </label>

                  <label>
                    Monto
                    <input
                      type="number"
                      required
                      min="0.01"
                      step="0.01"
                      value={lineAmount}
                      onChange={(event) => setLineAmount(event.target.value)}
                    />
                  </label>

                  <label>
                    Referencia
                    <input
                      value={lineReference}
                      onChange={(event) => setLineReference(event.target.value)}
                    />
                  </label>

                  <label className="bank-field-wide">
                    Descripción
                    <input
                      value={lineDescription}
                      onChange={(event) =>
                        setLineDescription(event.target.value)
                      }
                    />
                  </label>

                  <div className="bank-form-actions">
                    <button
                      className="primary-button"
                      type="submit"
                      disabled={busy}
                    >
                      {busy ? "Guardando…" : "Agregar línea"}
                    </button>
                  </div>
                </form>
              ) : null}

              <div className="bank-lines">
                {detail.lines.map((line) => {
                  const expectedDirection =
                    line.line_type === "credit"
                      ? ["income", "transfer_in"]
                      : ["expense", "transfer_out"];

                  const candidates = detail.available_movements.filter(
                    (movement) =>
                      Number(movement.amount) === Number(line.amount) &&
                      expectedDirection.includes(movement.movement_type),
                  );

                  return (
                    <article className="bank-line" key={line.id}>
                      <div>
                        <div className="quotation-actions">
                          <span className="status-badge">{line.status}</span>
                          {Number(line.correction_count || 0) > 0 ? (
                            <span className="status-badge">
                              {Number(line.correction_count)} corrección(es)
                            </span>
                          ) : null}
                        </div>
                        <strong>
                          {line.description || line.bank_reference || "Movimiento bancario"}
                        </strong>
                        <small>
                          {line.transaction_date} · {line.line_type}
                          {line.external_id ? ` · ${line.external_id}` : ""}
                        </small>
                        {line.ignore_reason ? (
                          <small>Motivo: {line.ignore_reason}</small>
                        ) : null}
                      </div>

                      <strong>
                        {money(line.amount, line.currency_code)}
                      </strong>

                      {line.status === "pending" && canEditSelected ? (
                        <div className="bank-line-actions">
                          <select
                            defaultValue=""
                            onChange={(event) => {
                              if (event.target.value) {
                                void matchLine(line, event.target.value);
                              }
                            }}
                            disabled={busy}
                          >
                            <option value="">Vincular movimiento…</option>
                            {candidates.map((movement) => (
                              <option key={movement.id} value={movement.id}>
                                #{movement.movement_number} ·{" "}
                                {movement.description}
                              </option>
                            ))}
                          </select>

                          <button
                            className="compact-button"
                            type="button"
                            disabled={busy}
                            onClick={() => void ignoreLine(line.id)}
                          >
                            Ignorar
                          </button>
                        </div>
                      ) : null}

                      {canEditSelected && line.status !== "matched" ? (
                        <button
                          className="compact-button"
                          type="button"
                          disabled={busy}
                          onClick={() => startEditLine(line)}
                        >
                          Editar
                        </button>
                      ) : null}

                      {line.status === "matched" && canEditSelected ? (
                        <button
                          className="compact-button"
                          type="button"
                          disabled={busy}
                          onClick={() => void unmatchLine(line.id)}
                        >
                          Desvincular
                        </button>
                      ) : null}

                      {lineEdit?.id === line.id ? (
                        <form
                          className="bank-line-edit-form"
                          onSubmit={updateStatementLine}
                        >
                          <label>
                            Fecha
                            <input
                              type="date"
                              required
                              value={lineEdit.transaction_date}
                              onChange={(event) =>
                                setLineEdit((current) =>
                                  current
                                    ? {
                                        ...current,
                                        transaction_date: event.target.value,
                                      }
                                    : current,
                                )
                              }
                            />
                          </label>

                          <label>
                            Fecha valor
                            <input
                              type="date"
                              value={lineEdit.value_date}
                              onChange={(event) =>
                                setLineEdit((current) =>
                                  current
                                    ? {
                                        ...current,
                                        value_date: event.target.value,
                                      }
                                    : current,
                                )
                              }
                            />
                          </label>

                          <label>
                            Tipo
                            <select
                              value={lineEdit.line_type}
                              onChange={(event) =>
                                setLineEdit((current) =>
                                  current
                                    ? {
                                        ...current,
                                        line_type: event.target.value as
                                          | "credit"
                                          | "debit",
                                      }
                                    : current,
                                )
                              }
                            >
                              <option value="credit">Abono / crédito</option>
                              <option value="debit">Cargo / débito</option>
                            </select>
                          </label>

                          <label>
                            Monto
                            <input
                              type="number"
                              required
                              min="0.01"
                              step="0.01"
                              value={lineEdit.amount}
                              onChange={(event) =>
                                setLineEdit((current) =>
                                  current
                                    ? { ...current, amount: event.target.value }
                                    : current,
                                )
                              }
                            />
                          </label>

                          <label>
                            Referencia
                            <input
                              value={lineEdit.bank_reference}
                              onChange={(event) =>
                                setLineEdit((current) =>
                                  current
                                    ? {
                                        ...current,
                                        bank_reference: event.target.value,
                                      }
                                    : current,
                                )
                              }
                            />
                          </label>

                          <label>
                            ID externo
                            <input
                              value={lineEdit.external_id}
                              onChange={(event) =>
                                setLineEdit((current) =>
                                  current
                                    ? {
                                        ...current,
                                        external_id: event.target.value,
                                      }
                                    : current,
                                )
                              }
                            />
                          </label>

                          <label className="bank-field-wide">
                            Descripción
                            <input
                              value={lineEdit.description}
                              onChange={(event) =>
                                setLineEdit((current) =>
                                  current
                                    ? {
                                        ...current,
                                        description: event.target.value,
                                      }
                                    : current,
                                )
                              }
                            />
                          </label>

                          <label className="bank-field-wide">
                            Motivo de corrección
                            <textarea
                              required
                              minLength={10}
                              rows={3}
                              value={lineEdit.reason}
                              onChange={(event) =>
                                setLineEdit((current) =>
                                  current
                                    ? { ...current, reason: event.target.value }
                                    : current,
                                )
                              }
                              placeholder="Explica por qué se corrige esta línea."
                            />
                          </label>

                          <div className="bank-form-actions">
                            <button
                              className="primary-button"
                              type="submit"
                              disabled={busy}
                            >
                              {busy ? "Guardando…" : "Guardar corrección"}
                            </button>
                            <button
                              className="ghost-button"
                              type="button"
                              disabled={busy}
                              onClick={() => setLineEdit(null)}
                            >
                              Cancelar
                            </button>
                          </div>
                        </form>
                      ) : null}
                    </article>
                  );
                })}
              </div>

              {detail.history.length > 0 ? (
                <section className="bank-history">
                  <div className="section-heading">
                    <div>
                      <span className="eyebrow">Auditoría</span>
                      <h3>Historial de estados</h3>
                    </div>
                  </div>

                  <div className="bank-history-list">
                    {detail.history.map((entry) => (
                      <article className="bank-history-item" key={entry.id}>
                        <div>
                          <strong>
                            {entry.old_status
                              ? `${entry.old_status} → ${entry.new_status}`
                              : entry.new_status}
                          </strong>
                          <small>
                            {new Date(entry.changed_at).toLocaleString("es-PE")}
                          </small>
                        </div>
                        {entry.reason ? <p>{entry.reason}</p> : null}
                      </article>
                    ))}
                  </div>
                </section>
              ) : null}

              {selectedRow ? (
                <div className="bank-footer-status">
                  <span>
                    Pendientes: {Number(selectedRow.pending_lines)}
                  </span>
                  <span>
                    Conciliadas: {Number(selectedRow.matched_lines)}
                  </span>
                  <span>
                    Ignoradas: {Number(selectedRow.ignored_lines)}
                  </span>
                </div>
              ) : null}
            </>
          )}
        </section>
      </section>
    </div>
  );
}
